import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Workflow, Plus, Loader2, Trash2, Zap, ArrowRight } from "lucide-react";

const TRIGGERS = ["Lead created", "Lead assigned", "Lead SLA breached", "Deal won", "Deal lost", "Quote accepted", "Invoice created", "Payment received", "Payment failed", "Ticket created", "Ticket overdue", "Task completed", "SLA at risk", "SLA breached", "Shift started", "Renewal approaching"];
const ACTIONS = ["Assign owner", "Reassign", "Create task", "Create ticket", "Create invoice", "Send email", "Send WhatsApp", "Send notification", "Change status", "Escalate to manager", "Start workflow", "Request approval", "Create renewal", "Add tag"];

export default function Automation() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", trigger: TRIGGERS[0], condition: "", action: ACTIONS[0] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const { data: rules = [], isLoading } = useQuery({ queryKey: ["automation-rules"], queryFn: async () => (await api.get("/automation-rules")).data });

  const createMut = useMutation({
    mutationFn: (body) => api.post("/automation-rules", { ...body, active: true, status: "active" }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-rules"] }); setOpen(false); toast.success("Automation rule created"); },
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, active }) => api.patch(`/automation-rules/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["automation-rules"] }),
  });
  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/automation-rules/${id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["automation-rules"] }); toast.success("Rule deleted"); },
  });

  return (
    <div>
      <PageHeader title="Automation" subtitle="Event-driven rules: when this happens, do that" testid="automation-header">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full" data-testid="new-rule-btn"><Plus className="mr-1.5 h-4 w-4" /> New Rule</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Create automation rule</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Rule name</Label><Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Auto-assign new Meta leads" data-testid="rule-name-input" /></div>
              <div className="space-y-1.5"><Label>When (trigger)</Label>
                <Select value={f.trigger} onValueChange={(v) => set("trigger", v)}><SelectTrigger data-testid="rule-trigger-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{TRIGGERS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="space-y-1.5"><Label>Condition (optional)</Label><Input value={f.condition} onChange={(e) => set("condition", e.target.value)} placeholder="Page = Certon Logistic" data-testid="rule-condition-input" /></div>
              <div className="space-y-1.5"><Label>Then (action)</Label>
                <Select value={f.action} onValueChange={(v) => set("action", v)}><SelectTrigger data-testid="rule-action-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
              </div>
              <DialogFooter><Button onClick={() => createMut.mutate(f)} disabled={createMut.isPending || !f.name} data-testid="rule-save-btn">{createMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create rule"}</Button></DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {isLoading ? <Loading /> : rules.length === 0 ? (
        <EmptyState icon={Workflow} title="No automation rules yet" description="Automate assignments, notifications, follow-ups and escalations based on business events."
          action={<Button onClick={() => setOpen(true)} className="rounded-full"><Plus className="mr-1.5 h-4 w-4" /> New Rule</Button>} testid="rules-empty" />
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <Card key={r.id} className="flex items-center gap-4 p-4" data-testid={`rule-${r.id}`}>
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-automation/10 text-automation"><Zap className="h-5 w-5" /></span>
              <div className="min-w-0 flex-1">
                <p className="font-medium">{r.name}</p>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="rounded bg-info/10 px-1.5 py-0.5 text-info">{r.trigger}</span>
                  {r.condition && <span className="rounded bg-muted px-1.5 py-0.5">if {r.condition}</span>}
                  <ArrowRight className="h-3 w-3" />
                  <span className="rounded bg-automation/10 px-1.5 py-0.5 text-automation">{r.action}</span>
                </div>
              </div>
              <Switch checked={r.active} onCheckedChange={(v) => toggleMut.mutate({ id: r.id, active: v })} data-testid={`rule-toggle-${r.id}`} />
              <Button variant="ghost" size="icon" onClick={() => deleteMut.mutate(r.id)} data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
